/**
 * LEGAL CONSENT — the boundary, and the honest answer to "is it stored?".
 *
 * ============================================================================
 * NO. CONSENT IS NOT PERSISTED ANYWHERE, AND `record()` DOES NOT RESOLVE. It builds
 * the exact request a real endpoint would receive and then throws
 * `ConsentUnavailableError`. The brief's words were "Do not fake persistence", and a
 * resolved promise here would be precisely that — the signup form would look as
 * though it had filed a consent record, and the record would not exist.
 * ============================================================================
 *
 * WHY THIS ONE REFUSES WHERE authApi.signup RESOLVES
 * --------------------------------------------------
 * They are not the same kind of lie, and the difference is worth being exact about.
 * `authApi.signup` fabricates a *session* — a client-side object that lives in a
 * Zustand store, is gone on reload, and whose whole purpose is to let the UI be used.
 * Nobody is harmed by a simulated session, and the app says everywhere that auth is
 * simulated.
 *
 * A CONSENT RECORD IS EVIDENCE. Its only function is to be produced later — when a
 * regulator asks whether consent was obtained, or a user says they never agreed to
 * marketing email. A record that is not durably stored has no function at all: it
 * cannot be produced, it cannot be audited, and it cannot be withdrawn against. So a
 * `Promise<{ id }>` from a mock would not be a stand-in for the real thing the way a
 * fixture Song stands in for a Song. It would be a claim that the compliance
 * obligation has been met. Same reasoning as api/audioUploadApi.ts: some results have
 * no truthful mock, and the correct mock is a refusal that names what is missing.
 *
 * WHAT THE APPLICATION *DOES* DO WITH CONSENT, all of it real:
 *   - refuses to create an account unless `acceptedLegal` is true (routes/Login.tsx);
 *   - keeps the two decisions separate, with marketing defaulting to false and no
 *     copy implying it is required;
 *   - stamps the instant and the document versions the user was actually shown
 *     (`buildConsentRecord`), so the record is auditable in shape even though it has
 *     nowhere to go;
 *   - carries the record to this boundary and reports, on screen, that the filing did
 *     not happen.
 *
 * The gate is genuine. The filing is not available. Those are different claims and the
 * UI makes both of them.
 *
 * WHAT THE BACKEND HAS TO PROVIDE — `describeConsentBlockers()` is the machine-readable
 * version and what the UI renders:
 *
 *   1. A BACKEND AT ALL. `VITE_API_BASE_URL` is unset. Configuration only.
 *
 *   2. AN APPEND-ONLY CONSENT LOG. Not a boolean column on the user row: a row per
 *      decision, with the timestamp, the document versions, the IP and user agent, and
 *      no UPDATE path — because the question a consent log answers is "what was true
 *      at 14:02 on the 3rd", and a mutable flag cannot answer it. Withdrawal is a new
 *      row saying withdrawn, not an edit to the row saying granted.
 *
 *   3. THE DOCUMENTS UNDER VERSION CONTROL SERVER-SIDE. A record naming "Terms 1.0" is
 *      worth nothing unless the text of 1.0 can be produced. Today the versions live in
 *      data/legal.ts and ship in the bundle, so an edit to a clause silently changes
 *      what "1.0" means and makes every prior acceptance record inaccurate. The
 *      documents need to be stored, immutable per version, and served.
 *
 *   4. A MARKETING SUPPRESSION LIST that the email provider actually reads. An opt-in
 *      flag nobody checks before sending is worse than no flag, and the withdrawal
 *      path (Settings, and an unsubscribe link) has to write to the same place.
 *
 * Note that 2, 3 and 4 do not appear the moment a base URL is configured. That is why
 * they are unconditional below: "add a backend" is not the whole fix.
 *
 * See docs/api-contract.md §25 for the endpoints this becomes.
 */
import { API_BASE_URL } from '../config';
import { LEGAL_DOCUMENTS } from '../data/legal';
import type { ConsentRecord } from '../types/domain';

/**
 * One reason a consent record cannot be filed from this application.
 *
 * The same shape as `AudioUploadBlocker` and `LyriaBlocker`, deliberately: every
 * boundary in this app that has to refuse answers "what is missing?" in one form, and
 * the UI already knows how to render a list of them.
 */
export interface ConsentBlocker {
  id: 'no-backend' | 'no-consent-log' | 'no-document-versioning' | 'no-suppression-list';
  /** One line, in the words a reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/** Everything that has to be true before consent can be filed, and is not. */
export function describeConsentBlockers(): ConsentBlocker[] {
  const blockers: ConsentBlocker[] = [];

  if (!API_BASE_URL) {
    blockers.push({
      id: 'no-backend',
      summary: 'There is no backend configured, so there is nowhere to file the consent record.',
      resolution:
        'Set VITE_API_BASE_URL and implement POST /consent. Every module in src/api/ is a ' +
        'fixture until then.',
    });
  }

  /*
    Unconditional. None of the three is implied by a configured base URL, and a reader
    who saw only the first blocker would think shipping a server finished the job.
  */
  blockers.push({
    id: 'no-consent-log',
    summary:
      'There is no append-only consent log. Nothing records that a user accepted the Terms, ' +
      'when, or at which version.',
    resolution:
      'A consent table with one immutable row per decision (granted and withdrawn alike), ' +
      'carrying the timestamp, document versions, IP and user agent. No UPDATE path.',
  });

  blockers.push({
    id: 'no-document-versioning',
    summary:
      'The legal documents ship in the JavaScript bundle, so a version label does not pin ' +
      'any particular text.',
    resolution:
      'Store each document version server-side and immutably, and serve it. A record naming ' +
      '"Terms 1.0" is only evidence if the text of 1.0 can be produced.',
  });

  blockers.push({
    id: 'no-suppression-list',
    summary:
      'Nothing consumes the marketing opt-in. There is no email provider and no suppression ' +
      'list, so opting out is not enforced by anything.',
    resolution:
      'A suppression list the sending provider reads before every campaign, written to by ' +
      'signup, by Settings, and by the unsubscribe link.',
  });

  return blockers;
}

/**
 * Thrown by `consentApi.record`.
 *
 * Its own class rather than `ApiError`, for the reason `AudioUploadUnavailableError`
 * gives: nothing was sent and no status came back, so there is nothing to retry, and a
 * caller that cannot tell "the server rejected this" from "this cannot be asked for
 * yet" will offer a Try again button for a permanent condition.
 *
 * Carries the record that WAS built, so a bug report — or a test — can show exactly
 * what would have gone on the wire.
 */
export class ConsentUnavailableError extends Error {
  readonly name = 'ConsentUnavailableError';

  /** What would have to change, from describeConsentBlockers(). */
  readonly blockers: ConsentBlocker[];

  /** The record that was built and not filed. Never sent anywhere. */
  readonly record: ConsentRecord | null;

  constructor(message: string, blockers: ConsentBlocker[], record: ConsentRecord | null = null) {
    super(message);
    this.blockers = blockers;
    this.record = record;
  }
}

/** What the signup form collected, before it is stamped into a record. */
export interface ConsentInput {
  /** The required Terms + Privacy acknowledgement. Must be true. */
  acceptedLegal: boolean;
  /** The optional product-email opt-in. */
  marketingOptIn: boolean;
}

/**
 * Stamps a submitted form into an auditable `ConsentRecord`.
 *
 * Exported and pure so the shape can be asserted without a server, matching
 * `buildAudioUploadRequest` and `buildImageMusicRequest`. Being able to pin the exact
 * record is what distinguishes a real boundary from an intention.
 *
 * THE VERSIONS ARE READ FROM data/legal.ts RATHER THAN PASSED IN. The record must name
 * the documents the user was actually shown, and the pages render from the same
 * constants — so reading them here means the record cannot drift from the text on
 * screen, which it could if a caller supplied a literal.
 *
 * `at` is a parameter with no default rather than an internal `new Date()`, because a
 * timestamp a function invents is untestable, and this one is the field that makes the
 * record evidence.
 */
export function buildConsentRecord(input: ConsentInput, at: Date): ConsentRecord {
  return {
    acceptedLegal: input.acceptedLegal,
    marketingOptIn: input.marketingOptIn,
    acceptedAt: at.toISOString(),
    termsVersion: LEGAL_DOCUMENTS.terms.version,
    privacyVersion: LEGAL_DOCUMENTS.privacy.version,
  };
}

/** What a caller needs to know before it promises the user anything about consent. */
export interface ConsentSupport {
  /** Whether a record can actually be filed. Always false today. */
  available: boolean;
  /** Everything missing, in order. Empty only when consent is genuinely stored. */
  blockers: ConsentBlocker[];
}

/**
 * The sentence the signup form shows beneath the checkboxes.
 *
 * Exported so the form and its test name the same string, and so the wording — which is
 * a statement to the user about what did NOT happen — has one definition. Deliberately
 * about the record and not about the account: the account is created, and saying
 * otherwise would be its own inaccuracy.
 */
export const CONSENT_NOT_STORED_NOTICE =
  'Your acceptance is applied to this session but is not filed anywhere: this build has no ' +
  'backend, so there is no consent record to keep. See src/api/consentApi.ts.';

/**
 * What the signup form says when Create account is pressed without the legal box ticked.
 *
 * Here rather than in routes/Login.tsx because a `.tsx` file exporting a constant trips
 * `react-refresh/only-export-components` (eslint.config.js), and because it belongs with
 * the other consent copy — the form and its test then name the same sentence. It says
 * what to do rather than only that something is wrong, since the control it refers to is
 * directly above it.
 */
export const CONSENT_REQUIRED_MESSAGE =
  'Please accept the Terms of Service and Privacy Policy to create an account';

export const consentApi = {
  /** What the consent path can do right now, for the UI to read. */
  support(): ConsentSupport {
    return {
      available: consentApi.supports(),
      blockers: describeConsentBlockers(),
    };
  },

  /**
   * Whether consent can be durably recorded. False, always, today.
   *
   * Derived from the blocker list rather than from `USE_MOCK_API`, so it cannot get out
   * of step with what the notice on screen says, and so configuring a base URL does not
   * silently claim a consent log exists.
   */
  supports(): boolean {
    return describeConsentBlockers().length === 0;
  },

  /**
   * NOT IMPLEMENTED — POST /consent.
   *
   * Throws `ConsentUnavailableError` with the built record attached. Typed
   * `Promise<never>` rather than `Promise<StoredConsent>`: there is no success shape
   * yet, and inventing one would let a caller be written against a result that never
   * comes.
   *
   * THE CALLER MUST NOT TREAT THIS REJECTION AS A SIGNUP FAILURE. The user did accept
   * the Terms — the gate on that is real and enforced before this is ever called. What
   * failed is our ability to file the acceptance, which is our problem and not theirs,
   * so routes/Login.tsx proceeds with the account and surfaces the storage gap
   * separately. Blocking signup on a missing backend would punish the user for our
   * incompleteness.
   */
  async record(input: ConsentInput, at: Date): Promise<never> {
    /*
      Built before the refusal, on purpose, as audioUploadApi.upload does: building the
      record is the part that works, and attaching it to the error means what failed is
      the dispatch rather than the integration.
    */
    const record = buildConsentRecord(input, at);
    throw new ConsentUnavailableError(
      'Consent cannot be recorded: there is no consent store. ' +
        'The acceptance was collected and enforced, but not filed. See src/api/consentApi.ts.',
      describeConsentBlockers(),
      record
    );
  },
};
